import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Brain, TrendingUp, TrendingDown, AlertTriangle, Package, RefreshCw, BarChart3 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function InventoryForecastPage() {
  const { t } = useLanguage();
  const { branches, fetchBranches } = useBranchStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState('all');
  const [days, setDays] = useState('30');

  useEffect(() => { fetchBranches(); fetchForecast(); }, []);

  const fetchForecast = async (bid, d) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const branch = bid || branchId;
      if (branch && branch !== 'all') params.append('branch_id', branch);
      params.append('days', d || days);
      const res = await api.get(`/v1/inventory/forecast?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load forecast'); }
    finally { setLoading(false); }
  };

  const handleBranchChange = (v) => { setBranchId(v); fetchForecast(v, days); };
  const handleDaysChange = (v) => { setDays(v); fetchForecast(branchId, v); };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inventory-forecast-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-200/50">

                <Brain size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Inventory Forecast</h1>

                <p className="text-sm text-muted-foreground mt-0.5">AI-powered inventory forecasting</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground">{t('inv_forecast_subtitle') || 'AI-powered demand prediction & reorder suggestions'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={branchId} onValueChange={handleBranchChange}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="branch-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={days} onValueChange={handleDaysChange}>
              <SelectTrigger className="w-24 h-8 text-xs" data-testid="days-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => fetchForecast()} data-testid="refresh-btn"><RefreshCw size={12} className="mr-1" />Refresh</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" size={32} /></div>
        ) : !data || !data.summary ? (
          <Card><CardContent className="py-12 text-center"><Package size={48} className="mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No sales data available for forecasting</p></CardContent></Card>
        ) : (
          <>
            {/* KPI Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase text-muted-foreground">Avg Daily Sales</p>
                  <p className="text-xl font-bold text-purple-700" data-testid="avg-daily-sales">{cur()} {data.summary.avg_daily_sales.toLocaleString()}</p>
                </CardContent>
              </Card>
              <Card className={`bg-gradient-to-br ${data.summary.trend_percentage >= 0 ? 'from-emerald-50 to-white border-success/30' : 'from-red-50 to-white border-error/30'}`}>
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase text-muted-foreground">90-Day Trend</p>
                  <p className={`text-xl font-bold flex items-center gap-1 ${data.summary.trend_percentage >= 0 ? 'text-success' : 'text-error'}`} data-testid="trend">
                    {data.summary.trend_percentage >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    {data.summary.trend_percentage > 0 ? '+' : ''}{data.summary.trend_percentage.toFixed(1)}%
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-white border-info/30">
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase text-muted-foreground">Items Tracked</p>
                  <p className="text-xl font-bold text-info" data-testid="items-tracked">{data.summary.total_items_tracked}</p>
                </CardContent>
              </Card>
              <Card className={`bg-gradient-to-br ${data.summary.items_needing_reorder > 0 ? 'from-amber-50 to-white border-warning/30' : 'from-emerald-50 to-white border-success/30'}`}>
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase text-muted-foreground">Need Reorder</p>
                  <p className={`text-xl font-bold ${data.summary.items_needing_reorder > 0 ? 'text-warning' : 'text-success'}`} data-testid="need-reorder">
                    {data.summary.items_needing_reorder > 0 && <AlertTriangle size={16} className="inline mr-1" />}
                    {data.summary.items_needing_reorder}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Sales Forecast Chart */}
            {data.forecast && data.forecast.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><BarChart3 size={16} />Projected Sales ({days} days)</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={data.forecast}>
                      <defs>
                        <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => [`${cur()} ${v.toLocaleString()}`, 'Projected']} labelFormatter={l => `Date: ${l}`} />
                      <Area type="monotone" dataKey="projected_sales" stroke="#8b5cf6" fill="url(#forecastGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* AI Analysis */}
            {data.ai_analysis && (
              <Card className="border-purple-200 bg-purple-50/30">
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Brain size={16} className="text-purple-600" />AI Insights</CardTitle></CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none text-foreground whitespace-pre-wrap" data-testid="ai-analysis">{data.ai_analysis}</div>
                </CardContent>
              </Card>
            )}

            {/* Reorder Suggestions */}
            {data.reorder_suggestions && data.reorder_suggestions.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Package size={16} />Demand & Reorder Suggestions</CardTitle></CardHeader>
                <CardContent>
                  {/* Top demand bar chart */}
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.reorder_suggestions.slice(0, 10)} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="item_name" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="total_sold_90d" name="Units Sold (90d)" radius={[0, 4, 4, 0]}>
                        {data.reorder_suggestions.slice(0, 10).map((item, i) => (
                          <Cell key={i} fill={item.reorder_needed ? '#f59e0b' : '#10b981'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Table */}
                  <div className="mt-4 hidden md:block overflow-x-auto">
                    <table className="w-full text-xs" data-testid="reorder-table">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">Item</th>
                          <th className="text-right p-2">Sold (90d)</th>
                          <th className="text-right p-2">Daily Demand</th>
                          <th className="text-right p-2">Projected ({days}d)</th>
                          <th className="text-right p-2">Stock</th>
                          <th className="text-right p-2">Days Left</th>
                          <th className="text-right p-2">Reorder Qty</th>
                          <th className="text-center p-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.reorder_suggestions.map((item, i) => (
                          <tr key={i} className={i % 2 ? 'bg-muted/50' : ''}>
                            <td className="p-2 font-medium">{item.item_name}</td>
                            <td className="p-2 text-right">{item.total_sold_90d}</td>
                            <td className="p-2 text-right">{item.daily_demand}</td>
                            <td className="p-2 text-right">{item.projected_demand}</td>
                            <td className="p-2 text-right">{item.current_stock}</td>
                            <td className={`p-2 text-right font-bold ${item.days_until_stockout < 14 ? 'text-error' : item.days_until_stockout < 30 ? 'text-warning' : 'text-success'}`}>{item.days_until_stockout}</td>
                            <td className="p-2 text-right font-bold text-purple-600">{item.suggested_reorder_qty || '-'}</td>
                            <td className="p-2 text-center">
                              <Badge variant={item.reorder_needed ? 'destructive' : 'default'} className="text-[10px]">
                                {item.reorder_needed ? 'REORDER' : 'OK'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile cards */}
                  <div className="md:hidden space-y-2 mt-4">
                    {data.reorder_suggestions.map((item, i) => (
                      <div key={i} className="p-2 border rounded-lg text-xs">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{item.item_name}</span>
                          <Badge variant={item.reorder_needed ? 'destructive' : 'default'} className="text-[9px]">{item.reorder_needed ? 'REORDER' : 'OK'}</Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 mt-1 text-muted-foreground">
                          <span>Sold: {item.total_sold_90d}</span>
                          <span>Stock: {item.current_stock}</span>
                          <span>Days: {item.days_until_stockout}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
